import { TFnType, useTranslate } from '@tolgee/react';

/**
 * Native-language label for the ELA-equivalent subject in non-native-English
 * countries. Curipod treats English / Literacy / "{native language}" as the
 * same underlying subject ('ela') — the native option just lets the teacher
 * pick the one that matches what they call the subject locally.
 *
 * Shared by the home-page subject dropdown and the onboarding modal so both
 * surfaces show the same list. Add a new country here once and it's picked
 * up everywhere.
 */
export const NATIVE_LANG_BY_COUNTRY: Record<string, string> = {
  NO: 'Norsk',
  SE: 'Svenska',
  DK: 'Dansk',
  FI: 'Suomi',
  DE: 'Deutsch',
  FR: 'Français',
  ES: 'Español',
  IT: 'Italiano',
  NL: 'Nederlands',
  PL: 'Polski',
  BR: 'Português',
  MX: 'Español',
  JP: '日本語',
  AE: 'العربية',
};

/**
 * Maps subject abbreviations to sentence-friendly display names.
 * Use this when you need a subject rendered in the middle of a sentence.
 */
export const formatSubjectForSentence = (
  subject: string | number | boolean | undefined,
): string | undefined => {
  if (!subject) return undefined;

  const subjectString = String(subject)
    .toLowerCase()
    .trim();

  const subjectMap: Record<string, string> = {
    ela: 'English',
    english: 'English',
    literacy: 'English',
    math: 'math',
    science: 'science',
    social_studies: 'social studies',
    socialstudies: 'social studies',
    world_languages: 'world languages',
    worldlanguages: 'world languages',
    mental_health: 'mental health',
    mentalhealth: 'mental health',
    other: '',
  };

  return subjectMap[subjectString] || String(subject);
};

/**
 * Maps subject abbreviations to localized sentence-friendly display names.
 * Use this when you need a subject rendered in the middle of a sentence with i18n support.
 */
export function useFormatLocalizedSubjectForSentence(
  subject: string | number | boolean | undefined,
): string | undefined {
  const { t } = useTranslate();
  if (!subject) return undefined;
  const subjectString = String(subject)
    .toLowerCase()
    .trim();

  // 'english' and 'literacy' must always render as the literal US-English
  // string 'English' regardless of the user's tolgee locale (CURI-301).
  if (subjectString === 'english' || subjectString === 'literacy') {
    return 'English';
  }

  const subjectMap: Record<string, string> = {
    ela: t('subject.ela', 'English'),
    math: t('subject.math', 'math'),
    science: t('subject.science', 'science'),
    social_studies: t('subject.socialStudies', 'social studies'),
    socialstudies: t('subject.socialStudies', 'social studies'),
    world_languages: t('subject.worldLanguages', 'world languages'),
    worldlanguages: t('subject.worldLanguages', 'world languages'),
    mental_health: t('subject.mentalHealth', 'mental health'),
    mentalhealth: t('subject.mentalHealth', 'mental health'),
    other: '',
  };

  return subjectMap[subjectString] ?? String(subject);
}
